/**
 * Navigation 컴포넌트
 */

import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { theme } from '../../../styles/theme';
import { mediaQueries } from '../../../constants/breakpoints';
import { ROUTES } from '../../../constants/routes';

interface NavigationProps {
  isMobileOpen: boolean;
  onClose: () => void;
}

const Nav = styled.nav<{ isMobileOpen: boolean }>`
  ${mediaQueries.tablet} {
    display: flex;
    gap: ${theme.spacing.md};
  }

  ${mediaQueries.maxTablet} {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 280px;
    background-color: ${theme.colors.background};
    box-shadow: ${theme.shadows.lg};
    transform: translateX(${({ isMobileOpen }) => (isMobileOpen ? '0' : '100%')});
    transition: transform 0.3s ease-in-out;
    padding: ${theme.spacing.xxxl} ${theme.spacing.lg};
    overflow-y: auto;
    z-index: 1000;
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  display: none;

  ${mediaQueries.maxTablet} {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const MenuList = styled.ul`
  list-style: none;
  display: flex;
  gap: ${theme.spacing.lg};

  ${mediaQueries.maxTablet} {
    flex-direction: column;
    gap: ${theme.spacing.md};
  }
`;

const MenuItem = styled.li`
  position: relative;

  &:hover > ul {
    display: block;
  }

  ${mediaQueries.maxTablet} {
    &:hover > ul {
      display: block;
    }
  }
`;

const MenuLink = styled(Link)`
  color: ${theme.colors.text};
  font-weight: 600;
  font-size: ${theme.fontSizes.md};
  text-decoration: none;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  display: block;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: ${theme.colors.secondary};
  }
`;

const SubMenuList = styled.ul`
  list-style: none;
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  background-color: ${theme.colors.background};
  box-shadow: ${theme.shadows.md};
  min-width: 200px;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} 0;

  ${mediaQueries.maxTablet} {
    position: static;
    box-shadow: none;
    padding-left: ${theme.spacing.lg};
    margin-top: ${theme.spacing.sm};
    display: block;
  }
`;

const SubMenuItem = styled.li``;

const SubMenuLink = styled(Link)`
  color: ${theme.colors.textLight};
  font-size: ${theme.fontSizes.sm};
  text-decoration: none;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  display: block;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${theme.colors.secondary};
    background-color: ${theme.colors.hover};
  }
`;

export const Navigation: React.FC<NavigationProps> = ({ isMobileOpen, onClose }) => {
  return (
    <>
      <Overlay isOpen={isMobileOpen} onClick={onClose} />
      <Nav isMobileOpen={isMobileOpen}>
        <MenuList>
          <MenuItem>
            <MenuLink to={ROUTES.CHURCH_INTRO.ABOUT} onClick={onClose}>
              교회 소개
            </MenuLink>
            <SubMenuList>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.CHURCH_INTRO.ABOUT} onClick={onClose}>
                  위례세움교회
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.CHURCH_INTRO.GREETING} onClick={onClose}>
                  인사의 말
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.CHURCH_INTRO.WORSHIP} onClick={onClose}>
                  예배 안내
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.CHURCH_INTRO.LOCATION} onClick={onClose}>
                  오시는 길
                </SubMenuLink>
              </SubMenuItem>
            </SubMenuList>
          </MenuItem>

          <MenuItem>
            <MenuLink to={ROUTES.SERMON.SUNDAY_MORNING} onClick={onClose}>
              설교 영상
            </MenuLink>
            <SubMenuList>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.SERMON.SUNDAY_MORNING} onClick={onClose}>
                  주일 오전예배
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.SERMON.SUNDAY_EVENING} onClick={onClose}>
                  주일 오후예배
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.SERMON.WEDNESDAY} onClick={onClose}>
                  수요예배
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.SERMON.DAWN} onClick={onClose}>
                  새벽기도회
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.SERMON.FRIDAY} onClick={onClose}>
                  금요성령집회
                </SubMenuLink>
              </SubMenuItem>
            </SubMenuList>
          </MenuItem>

          <MenuItem>
            <MenuLink to={ROUTES.SUNDAY_SCHOOL.INTRO} onClick={onClose}>
              교회학교
            </MenuLink>
            <SubMenuList>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.SUNDAY_SCHOOL.INTRO} onClick={onClose}>
                  교회학교 소개
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.SUNDAY_SCHOOL.BOARD} onClick={onClose}>
                  게시판
                </SubMenuLink>
              </SubMenuItem>
            </SubMenuList>
          </MenuItem>

          <MenuItem>
            <MenuLink to={ROUTES.DEPARTMENT.HALLELUJAH_CHOIR} onClick={onClose}>
              부서 안내
            </MenuLink>
            <SubMenuList>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.DEPARTMENT.HALLELUJAH_CHOIR} onClick={onClose}>
                  할렐루야 찬양대
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.DEPARTMENT.WIRYE_CHOIR} onClick={onClose}>
                  위례 소년소녀 합창단
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.DEPARTMENT.PRAISE_TEAM} onClick={onClose}>
                  찬양팀
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.DEPARTMENT.AGAPE} onClick={onClose}>
                  애찬부
                </SubMenuLink>
              </SubMenuItem>
            </SubMenuList>
          </MenuItem>

          {/* 새가족 */}
          {/* <MenuItem>
            <MenuLink to={ROUTES.NEW_FAMILY} onClick={onClose}>
              새가족
            </MenuLink>
          </MenuItem> */}

          <MenuItem>
            <MenuLink to={ROUTES.COMMUNITY.NEWS} onClick={onClose}>
              커뮤니티
            </MenuLink>
            <SubMenuList>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.COMMUNITY.NEWS} onClick={onClose}>
                  교회소식
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.COMMUNITY.GALLERY} onClick={onClose}>
                  갤러리
                </SubMenuLink>
              </SubMenuItem>
              <SubMenuItem>
                <SubMenuLink to={ROUTES.COMMUNITY.BULLETIN} onClick={onClose}>
                  주보
                </SubMenuLink>
              </SubMenuItem>
            </SubMenuList>
          </MenuItem>
        </MenuList>
      </Nav>
    </>
  );
};
